import {
  type ActivatePremiumRequest,
  type ActivatePremiumResponse,
  type ActivateTrialRequest,
  type ActivateTrialResponse,
  type CancelPremiumResponse,
  type CheckPremiumResponse,
} from './types'

/**
 * Client for Membership API
 * Used to check and manage premium membership status
 */
export class MembershipApiClient {
  private baseUrl = 'https://batesky-membership-system.attps.workers.dev'

  /**
   * Check if a user has premium membership
   */
  async checkPremium(userDid: string): Promise<CheckPremiumResponse> {
    const url = `${this.baseUrl}/check-premium`
    return this.post<CheckPremiumResponse>(url, {userDid})
  }

  /**
   * Activate premium membership for a user
   */
  async activatePremium(
    data: ActivatePremiumRequest,
  ): Promise<ActivatePremiumResponse> {
    const url = `${this.baseUrl}/activate-premium`
    return this.post<ActivatePremiumResponse>(url, data)
  }

  /**
   * Cancel premium membership for a user
   */
  async cancelPremium(userDid: string): Promise<CancelPremiumResponse> {
    const url = `${this.baseUrl}/cancel-premium`
    return this.post<CancelPremiumResponse>(url, {userDid})
  }

  /**
   * Activate trial membership for a user
   */
  async activateTrial(data: ActivateTrialRequest): Promise<ActivateTrialResponse> {
    const url = `${this.baseUrl}/activate-trial`
    return this.post<ActivateTrialResponse>(url, data)
  }

  /**
   * Internal POST method with error handling
   */
  private async post<T>(endpoint: string, data: unknown): Promise<T> {
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(
        `Membership API error (${response.status}): ${errorText || response.statusText}`,
      )
    }

    return response.json()
  }
}

// Singleton instance
export const membershipApiClient = new MembershipApiClient()
